import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../utils/axios';
import { toast } from 'sonner';
import { GitMerge, Loader2, ArrowLeft, Lock, Eye, Target, Users, ArrowRight } from 'lucide-react';

// ========== SIMPLIFIED NETWORK GRAPH ==========
const NetworkGraph = ({ cluster }) => {
  const [hovered, setHovered] = useState(null);
  const W = 900, H = 520;

  const { nodes, links } = useMemo(() => {
    const cases = cluster.casos || [];
    const edges = cluster.conexiones || [];
    if (!cases.length) return { nodes: [], links: [] };

    const nodeMap = {};
    const n = cases.length;

    // Layout cases in a circle
    cases.forEach((c, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const rx = W * 0.35, ry = H * 0.38;
      nodeMap[c.id] = {
        id: c.id, type: 'case',
        label: c.nombre || c.cuit || c.id.slice(0, 12),
        x: W / 2 + rx * Math.cos(angle),
        y: H / 2 + ry * Math.sin(angle),
        score: c.score, color: c.score >= 80 ? '#ef4444' : c.score >= 50 ? '#f59e0b' : '#3b82f6',
        r: 22,
      };
    });

    // Only show top 5 contrapartes as central hub nodes
    const topCP = (cluster.top_contrapartes || []).slice(0, 5);
    topCP.forEach((cp, i) => {
      if (!nodeMap[cp.cvu]) {
        const angle = (2 * Math.PI * i) / Math.max(topCP.length, 1);
        const r = Math.min(W, H) * 0.12;
        nodeMap[cp.cvu] = {
          id: cp.cvu, type: 'hub',
          label: cp.nombre || cp.cvu,
          x: W / 2 + r * Math.cos(angle),
          y: H / 2 + r * Math.sin(angle),
          txs: cp.total_txs, color: '#a855f7', r: 16,
        };
      }
    });

    // Build links - only strong ones
    const linksList = [];
    const seen = new Set();
    edges.forEach(e => {
      const key = [e.from, e.to].sort().join('-');
      if (seen.has(key)) return;
      seen.add(key);
      if (!nodeMap[e.from] || !nodeMap[e.to]) return;

      const hasDirect = e.razones?.some(r => r.tipo === 'Transferencia directa');
      const hasCP = e.razones?.some(r => r.tipo === 'Contraparte compartida');
      const hasSame = e.razones?.some(r => r.tipo?.includes('Mismo'));
      // Get shared counterparty name for label
      let cpLabel = '';
      e.razones?.forEach(r => {
        if (r.contrapartes?.length) {
          cpLabel = r.contrapartes[0].nombre || r.contrapartes[0].cvu || '';
        }
      });

      linksList.push({
        from: e.from, to: e.to,
        color: hasDirect ? '#ef4444' : hasSame ? '#10b981' : '#a855f7',
        width: hasDirect ? 3 : hasCP ? 2 : 1.5,
        label: hasDirect ? 'Transferencia directa' : hasSame ? (e.razones.find(r => r.tipo?.includes('Mismo'))?.detalle || 'Mismo cliente') : cpLabel ? `via ${cpLabel}` : 'Contraparte',
        opacity: hasDirect ? 0.8 : 0.4,
      });
    });

    return { nodes: Object.values(nodeMap), links: linksList };
  }, [cluster]);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full bg-slate-950 rounded-xl" style={{ minHeight: 420 }}>
        <defs>
          <marker id="arr" viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="7" markerHeight="5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" /></marker>
        </defs>

        {/* Links */}
        {links.map((link, i) => {
          const f = nodes.find(n => n.id === link.from);
          const t = nodes.find(n => n.id === link.to);
          if (!f || !t) return null;
          const isHL = hovered && (hovered === link.from || hovered === link.to);
          const dx = t.x - f.x, dy = t.y - f.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const x1 = f.x + (dx / dist) * f.r, y1 = f.y + (dy / dist) * f.r;
          const x2 = t.x - (dx / dist) * t.r, y2 = t.y - (dy / dist) * t.r;
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
          return (
            <g key={i} opacity={isHL ? 1 : hovered ? 0.1 : link.opacity}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={link.color} strokeWidth={isHL ? link.width + 1 : link.width} />
              {isHL && link.label && (
                <g>
                  <rect x={mx - 60} y={my - 10} width={120} height={18} rx={4} fill="#1e293b" opacity={0.9} />
                  <text x={mx} y={my + 3} textAnchor="middle" fill="#e2e8f0" fontSize={8} fontWeight={500}>{link.label}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node, i) => {
          const isHL = hovered === node.id;
          const isConnected = hovered && links.some(l => (l.from === hovered || l.to === hovered) && (l.from === node.id || l.to === node.id));
          const dimmed = hovered && !isHL && !isConnected;
          return (
            <g key={i} opacity={dimmed ? 0.15 : 1} className="cursor-pointer" onMouseEnter={() => setHovered(node.id)} onMouseLeave={() => setHovered(null)}>
              {/* Glow on hover */}
              {isHL && <circle cx={node.x} cy={node.y} r={node.r + 6} fill={node.color} opacity={0.2} />}
              <circle cx={node.x} cy={node.y} r={node.r} fill="#0f172a" stroke={node.color} strokeWidth={node.type === 'case' ? 3 : 2} />
              {/* Score or txs inside */}
              <text x={node.x} y={node.y + 4} textAnchor="middle" fill={node.color} fontSize={node.type === 'case' ? 12 : 9} fontWeight={700}>
                {node.type === 'case' ? node.score : node.txs}
              </text>
              {/* Label below */}
              <text x={node.x} y={node.y + node.r + 13} textAnchor="middle" fill={isHL ? '#f8fafc' : '#64748b'} fontSize={9} fontWeight={isHL ? 600 : 400}>
                {node.label}
              </text>
              {/* Type indicator */}
              {node.type === 'hub' && (
                <circle cx={node.x + node.r * 0.7} cy={node.y - node.r * 0.7} r={4} fill="#a855f7" stroke="#0f172a" strokeWidth={1} />
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex gap-4 text-[10px] text-slate-400 bg-slate-900/90 backdrop-blur-sm px-4 py-2 rounded-lg border border-slate-700">
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full border-2 border-red-500" /> Caso (alto riesgo)</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full border-2 border-blue-500" /> Caso</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full border-2 border-purple-500" /> Contraparte clave</span>
        <span className="flex items-center gap-1.5"><div className="w-6 border-t-2 border-red-500" /> Directo</span>
        <span className="flex items-center gap-1.5"><div className="w-6 border-t-2 border-purple-500" /> Via contraparte</span>
      </div>
      <div className="absolute top-3 right-3 text-[10px] text-slate-500 bg-slate-900/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-slate-700">
        Pasá el mouse sobre un nodo para ver sus conexiones
      </div>
    </div>
  );
};

// ========== MAIN PAGE ==========
export default function AnalisisVinculacion() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCluster, setActiveCluster] = useState(0);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (!ids) { setLoading(false); return; }
    const load = async () => {
      try {
        const res = await api.post('/cases/analizar-vinculacion', { case_ids: ids.split(',') });
        setData(res.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
      finally { setLoading(false); }
    };
    load();
  }, [searchParams]);

  const unificar = async (clusterObj) => {
    const ids = clusterObj.casos.map(c => c.id);
    if (!window.confirm(`Unificar ${ids.length} casos en uno solo?\nLos originales se cerrarán.`)) return;
    try {
      const res = await api.post('/cases/unificar', {
        case_ids: ids,
        analisis: {
          cluster: clusterObj.label, conexiones: clusterObj.conexiones,
          top_contrapartes: clusterObj.top_contrapartes, total_casos: clusterObj.total_casos,
          score_max: clusterObj.score_max, fecha_analisis: new Date().toISOString(),
        },
      });
      toast.success(res.data.message);
      navigate(`/m/gestion-de-casos/caso/${res.data.caso_padre_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin text-violet-500" size={32} /></div>;
  if (!data) return <div className="p-8 text-center text-slate-400">No se proporcionaron casos</div>;

  const cluster = data.clusters?.[activeCluster];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
            <GitMerge size={24} className="text-violet-600" />
            <div>
              <h1 className="text-lg font-bold text-slate-900">Análisis de Red</h1>
              <p className="text-xs text-slate-500">{data.total_casos} casos analizados</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="text-center px-4 py-2 bg-violet-50 rounded-lg border border-violet-200">
              <p className="text-xl font-bold text-violet-700">{data.total_clusters}</p>
              <p className="text-[10px] text-violet-500 uppercase">Redes</p>
            </div>
            <div className="text-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xl font-bold text-blue-700">{data.total_conexiones}</p>
              <p className="text-[10px] text-blue-500 uppercase">Conexiones</p>
            </div>
            <div className="text-center px-4 py-2 bg-slate-100 rounded-lg border border-slate-200">
              <p className="text-xl font-bold text-slate-600">{data.aislados?.length || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase">Aislados</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {data.total_clusters === 0 ? (
          <Card className="p-16 text-center"><Users size={40} className="mx-auto text-slate-300 mb-4" /><p className="text-slate-500">No se encontraron redes entre los casos seleccionados</p></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left sidebar */}
            <div className="lg:col-span-1 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Redes detectadas</p>
              {data.clusters.map((cl, i) => (
                <button key={i} onClick={() => setActiveCluster(i)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${activeCluster === i ? 'bg-violet-50 border-violet-300 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                  <span className="text-sm font-bold text-slate-800 block truncate">{cl.label || `Red ${i + 1}`}</span>
                  <div className="flex gap-2 mt-1 text-[10px] text-slate-500">
                    <span><strong className="text-violet-600">{cl.total_casos}</strong> casos</span>
                    <span><strong className={cl.score_max >= 80 ? 'text-red-600' : 'text-amber-600'}>{cl.score_max}</strong> score</span>
                    <span>{cl.conexiones?.length || 0} cnx</span>
                  </div>
                </button>
              ))}
              {data.aislados?.length > 0 && (
                <details className="mt-4">
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Sin relación ({data.aislados.length})</summary>
                  <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                    {data.aislados.map((c, i) => (
                      <div key={i} className="text-[10px] text-slate-400 px-2 py-1 bg-slate-50 rounded truncate cursor-pointer hover:bg-slate-100"
                        onClick={() => navigate(`/m/gestion-de-casos/caso/${c.id}`)}>{c.nombre || c.cuit}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Right: cluster detail */}
            {cluster && (
              <div className="lg:col-span-3 space-y-6">
                {/* Header */}
                <Card className="p-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{cluster.label}</h2>
                    <p className="text-xs text-slate-500">{cluster.total_casos} casos conectados | {cluster.conexiones?.length} conexiones</p>
                  </div>
                  <Button onClick={() => unificar(cluster)} className="bg-violet-600 hover:bg-violet-700 text-white">
                    <GitMerge size={14} className="mr-1" /> Unificar {cluster.total_casos} casos
                  </Button>
                </Card>

                {/* Graph */}
                <NetworkGraph cluster={cluster} />

                {/* Explanation: HOW they connect */}
                {cluster.conexiones?.length > 0 && (
                  <Card className="p-5">
                    <h3 className="text-sm font-bold text-slate-800 mb-4">Cómo se conectan</h3>
                    <div className="space-y-3">
                      {cluster.conexiones.slice(0, 12).map((cx, i) => {
                        const razones = cx.razones || [];
                        return (
                          <div key={i} className="p-3 rounded-lg border border-slate-100 bg-slate-50">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-bold text-slate-800">{cx.from_nombre || '?'}</span>
                              <ArrowRight size={14} className="text-slate-400 shrink-0" />
                              <span className="font-bold text-slate-800">{cx.to_nombre || '?'}</span>
                            </div>
                            <div className="mt-2 space-y-1">
                              {razones.map((r, ri) => (
                                <div key={ri} className="flex items-start gap-2 text-xs">
                                  <Badge className={`shrink-0 text-[9px] ${r.tipo === 'Transferencia directa' ? 'bg-red-100 text-red-700' : r.tipo === 'Contraparte compartida' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                    {r.tipo}
                                  </Badge>
                                  <span className="text-slate-600">{r.detalle}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {(cluster.conexiones?.length || 0) > 12 && (
                        <p className="text-xs text-slate-400 text-center">... y {cluster.conexiones.length - 12} conexiones más</p>
                      )}
                    </div>
                  </Card>
                )}

                {/* Top contrapartes */}
                {cluster.top_contrapartes?.length > 0 && (
                  <Card className="p-5">
                    <h3 className="text-sm font-bold text-purple-700 mb-3 flex items-center gap-2"><Target size={16} /> Contrapartes Clave</h3>
                    <p className="text-xs text-slate-500 mb-3">CVUs que aparecen en los movimientos de múltiples casos de esta red</p>
                    <div className="overflow-x-auto rounded-lg border border-purple-200">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-purple-50">
                          <th className="p-2.5 text-left font-semibold text-purple-700">CVU</th>
                          <th className="p-2.5 text-left font-semibold text-purple-700">Nombre</th>
                          <th className="p-2.5 text-right font-semibold text-purple-700">Transacciones</th>
                        </tr></thead>
                        <tbody>
                          {cluster.top_contrapartes.map((cp, i) => (
                            <tr key={i} className="border-t border-purple-100 hover:bg-purple-50/50">
                              <td className="p-2.5 font-mono text-[10px] text-slate-600">{cp.cvu}</td>
                              <td className="p-2.5 font-medium text-slate-800">{cp.nombre || <span className="italic text-slate-300">Sin identificar</span>}</td>
                              <td className="p-2.5 text-right font-bold text-purple-700">{cp.total_txs}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {/* Cases */}
                <Card className="p-5">
                  <h3 className="text-sm font-bold text-slate-800 mb-3">Casos del Grupo</h3>
                  <div className="space-y-1.5">
                    {cluster.casos.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-sm cursor-pointer transition-all text-xs"
                        onClick={() => navigate(`/m/gestion-de-casos/caso/${c.id}`)}>
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.score >= 80 ? '#ef4444' : c.score >= 50 ? '#f59e0b' : '#3b82f6' }} />
                          <span className="font-bold text-slate-800">{c.nombre || c.cuit}</span>
                          {c.tipos_alerta?.slice(0, 2).map((t, ti) => <Badge key={ti} className="bg-slate-200 text-slate-600 text-[9px]">{t}</Badge>)}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold tabular-nums" style={{ color: c.score >= 80 ? '#ef4444' : c.score >= 50 ? '#f59e0b' : '#3b82f6' }}>{c.score}</span>
                          <span className="text-slate-400">{c.alertas} alertas</span>
                          {c.bloqueada && <Lock size={12} className="text-red-500" />}
                          <Eye size={14} className="text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
